import { useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GrcGrid, gridOptions, DomLayout } from '@grc/ui-package';
import { IShipmentGridRow } from '../../../models/shipments/shipment-grid-row.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import { useI18nState } from '../../../stores/settings/language-context';
import { useWorkItemDetails } from './useWorkItemDetails';
import { useShipmentEntities } from '../useShipmentEntities';
import { useShipmentSettings } from '../useShipmentSettings';
import { getWorkItemDetailsGridColumns } from './work-item-details-grid.columns';
import { MyWorkItemsDetailsTab } from '../../../models/home/work-item.enum';
import '../grid/shipment-grid.scss';

interface IWorkItemDetailsGridProps {
  tab: MyWorkItemsDetailsTab;
  dateFilter: IDateRangeFilterValue;
  searchText?: string;
  onShipmentIdClick: (row: IShipmentGridRow) => void;
}

export default function WorkItemDetailsGrid({
  tab,
  dateFilter,
  searchText,
  onShipmentIdClick
}: IWorkItemDetailsGridProps) {
  const gridRef = useRef<AgGridReact<IShipmentGridRow>>(null);
  const { localeData } = useI18nState();
  const { rows, isLoading } = useWorkItemDetails(tab, dateFilter, searchText);
  const { destinationsMap, carriersMap, isLoading: entitiesLoading } =
    useShipmentEntities(rows);
  const { transportationModes, serviceCharges } = useShipmentSettings();

  const colDefs = useMemo(
    () =>
      getWorkItemDetailsGridColumns({
        localeData,
        destinationsMap,
        carriersMap,
        entitiesLoading,
        transportationModes,
        serviceCharges,
        onShipmentIdClick
      }),
    [
      localeData,
      destinationsMap,
      carriersMap,
      entitiesLoading,
      transportationModes,
      serviceCharges,
      onShipmentIdClick
    ]
  );

  useEffect(() => {
    if (isLoading) {
      gridRef.current?.api?.showLoadingOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, [isLoading]);

  return (
    <div
      className="shipment-grid-container grc-common-ag-grid"
      data-testid="work_item_details_grid"
    >
      <GrcGrid<any>
        colDefs={colDefs}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={rows}
        domLayout={DomLayout.Normal}
        isPagination
      />
    </div>
  );
}
